import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../services/apiClient';
import { useAuth } from '../contexts/AuthContext';

// 999.17990 (2): Approve stays disabled this long after a render where the
// page is visible+focused, so a click racing the render (or a popup opened
// in a background/unfocused tab) can't land before the user has actually
// seen the screen.
const APPROVE_ARM_DELAY_MS = 500;

/**
 * 999.17792 — MCP OAuth consent screen. /oauth/complete sends the browser here
 * (with ?oauth_state=) whenever the signed-in user has not yet approved THIS
 * pending authorization. Shows who is asking and where the code would go;
 * Approve / Deny call the Bearer-only /api/oauth/consent API and follow the
 * redirect it returns. Mounted behind ProtectedRoute in App.js, so a user is
 * always signed in here. nginx.conf sends X-Frame-Options DENY and
 * frame-ancestors 'none' so this page cannot be clickjacked.
 */
export default function ConsentPage() {
  const [searchParams] = useSearchParams();
  const oauthState = searchParams.get('oauth_state');
  const [request, setRequest] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [approveArmed, setApproveArmed] = useState(false);
  // ProtectedRoute (App.js) only renders children once `user` is set, so this
  // is never null here; users.email is NOT NULL/unique at the DB level
  // (auth-backend/src/db/migrations/20260320000001_create_users.js:15) and
  // /api/auth/profile always returns it — no fallback needed.
  const { user } = useAuth();

  useEffect(() => {
    // 999.18218 review r1: a new oauth_state on the same mount must not leave
    // the PREVIOUS request (its client_name / redirect_host) rendered with
    // Approve armed while decide() would post the NEW oauth_state — nor after
    // the new GET rejects. Drop it before fetching.
    setRequest(null);
    setApproveArmed(false);
    setError('');
    if (!oauthState) {
      setError('This authorization link is missing its request id.');
      return;
    }
    let cancelled = false;
    apiClient.get(`/api/oauth/consent?oauth_state=${encodeURIComponent(oauthState)}`)
      .then((data) => { if (!cancelled) setRequest(data); })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [oauthState]);

  // 999.17990 (2) round 2 (law): the arm timer used to start at MOUNT,
  // independent of the fetch effect above that renders the button — a
  // consent GET slower than APPROVE_ARM_DELAY_MS rendered Approve already
  // armed on first paint. Keyed off `request` now: the 500ms + visible/
  // focused gate only starts once the button is actually rendered.
  useEffect(() => {
    if (!request) return undefined;
    let timer = null;
    function evaluate() {
      if (timer) { clearTimeout(timer); timer = null; }
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        // 999.18218: disarm before re-arming — a new `request` (identity
        // change on the same mount) must wait the full delay again.
        setApproveArmed(false);
        timer = setTimeout(() => setApproveArmed(true), APPROVE_ARM_DELAY_MS);
      } else {
        setApproveArmed(false);
      }
    }
    evaluate();
    document.addEventListener('visibilitychange', evaluate);
    window.addEventListener('focus', evaluate);
    window.addEventListener('blur', evaluate);
    return () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', evaluate);
      window.removeEventListener('focus', evaluate);
      window.removeEventListener('blur', evaluate);
    };
  }, [request]);

  async function decide(decision) {
    setBusy(true);
    setError('');
    try {
      const data = await apiClient.post('/api/oauth/consent', { oauth_state: oauthState, decision });
      window.location.href = data.redirect;
    } catch (err) {
      setBusy(false);
      setError(err.message);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1>Authorize application</h1>
        {error && <div className="error-message" role="alert">{error}</div>}
        {!error && !request && <div className="loading">Loading...</div>}
        {request && (
          <>
            <p>
              <strong>{request.client_name === null ? 'An unnamed application' : request.client_name}</strong>
              {' '}
              {/* 999.17990 (3): client_name is attacker-controlled — set by the
                  client itself at public /oauth/register (ClientRegistrationPolicy.js),
                  never verified by us. Label it so an impersonating name doesn't
                  read as an endorsement. */}
              <span className="unverified-label">(unverified name, self-reported by the app)</span>
              {' '}is asking to access your Raike &amp; Sons account
              {' '}
              (<strong>{user.email}</strong>).
            </p>
            <p>If you approve, you will be sent to <strong>{request.redirect_host}</strong>.</p>
            <p>Only approve if you started this from that application yourself.</p>
            <div className="consent-actions">
              <button type="button" disabled={busy || !approveArmed} onClick={() => decide('approve')}>Approve</button>
              <button type="button" disabled={busy} onClick={() => decide('deny')}>Deny</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
